import React, { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import { isCustomer } from '../../../api';
import { trackEvent } from '../../../analytics';
import { sendInviteMessage, toApiError } from '../api';
import { useHangoutInvite, useInviteMessages } from '../hooks';
import { errorTitle, formatChatTime, formatInviteDate, formatInviteTime, loginUrlFor, MAX_CHAT_MESSAGE } from '../utils';
import { Avatar, EmptyState, ErrorState, Skeleton, VenueImage } from '../components/common';
import '../hangouts.css';

// Screen 6 "Group Chat" (/invites/:inviteId/chat). The server only lets the host and approved,
// going members in; everyone else gets an explanation instead of the messages.
const InviteChatPage: React.FC = () => {
  const { inviteId } = useParams<{ inviteId: string }>();
  const id = Number(inviteId) || null;
  const location = useLocation();
  const navigate = useNavigate();
  const signedIn = isCustomer();
  const invite = useHangoutInvite(signedIn ? id : null);
  const allowed = !!invite.data?.canChat;
  const chat = useInviteMessages(signedIn ? id : null, allowed);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const stickToBottom = useRef(true);

  useEffect(() => {
    if (allowed) trackEvent('hangout_chat_opened');
  }, [allowed]);

  // Follow new messages unless the person has scrolled up to read older ones.
  useEffect(() => {
    const el = listRef.current;
    if (el && stickToBottom.current) el.scrollTop = el.scrollHeight;
  }, [chat.messages.length]);

  if (!signedIn) return <Navigate to={loginUrlFor(location.pathname)} replace />;

  const send = async () => {
    const message = text.trim();
    if (!message || !id) return;
    setSending(true);
    setSendError(null);
    try {
      const sent = await sendInviteMessage(id, message);
      chat.addMessage(sent);
      stickToBottom.current = true;
      setText('');
      trackEvent('hangout_message_sent');
    } catch (err) {
      setSendError(toApiError(err).message);
    } finally {
      setSending(false);
    }
  };

  const inv = invite.data;
  if (invite.error) {
    const e = errorTitle(invite.error);
    return <main className="hg"><div className="hg-wrap"><ErrorState title={e.title} body={e.body} /></div></main>;
  }
  if (inv && !inv.canChat) {
    return (
      <main className="hg"><div className="hg-wrap">
        <EmptyState title="Group chat is for confirmed members" body={inv.inviteType === 'PrivateGroup' ? 'Once the host approves your request, you can chat with the group.' : 'Join the hangout to chat with everyone going.'}
          action={<Link className="hg-btn hg-gold" to={`/invite/${inv.publicToken}`}>View invite</Link>} />
      </div></main>
    );
  }

  const members = inv ? inv.counts.going : 0;
  return (
    <main className="hg" style={{ padding: 0 }}>
      <div className="hg-chat">
        <header className="hg-chat-head">
          <button type="button" className="hg-back" aria-label="Back" onClick={() => (inv ? navigate(`/invite/${inv.publicToken}`) : navigate(-1))}><ArrowBackIcon /></button>
          {inv ? <VenueImage url={inv.venue.imageUrl} alt={inv.venue.name} /> : <Skeleton height={48} />}
          <div style={{ minWidth: 0 }}>
            <b>{inv ? `${inv.venue.name} Night Out` : 'Group Chat'}</b>
            {inv && <small>{members} {members === 1 ? 'Member' : 'Members'} · {formatInviteDate(inv.inviteDateTime)} · {formatInviteTime(inv.inviteDateTime)}</small>}
          </div>
        </header>

        <div className="hg-chat-list" ref={listRef} aria-live="polite"
          onScroll={(e) => { const el = e.currentTarget; stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60; }}>
          {chat.hasOlder && <button type="button" className="hg-btn hg-outline hg-sm" style={{ alignSelf: 'center' }} onClick={() => { stickToBottom.current = false; chat.loadOlder(); }}>Load earlier messages</button>}
          {chat.loading && chat.messages.length === 0 && <><Skeleton height={54} width="70%" /><Skeleton height={54} width="60%" style={{ alignSelf: 'flex-end' }} /><Skeleton height={54} width="75%" /></>}
          {!chat.loading && chat.messages.length === 0 && <EmptyState title="No messages yet" body="Say hello to the group." />}
          {chat.error && <div className="hg-notice error">{chat.error.message}</div>}
          {chat.messages.map((m) => (
            <div key={m.id} className={`hg-msg ${m.isMe ? 'me' : ''}`}>
              {!m.isMe && <Avatar initials={m.senderInitials} small />}
              <div className="hg-bubble">
                <header><b>{m.isMe ? 'You' : m.senderName}{m.isHost ? ' · Host' : ''}</b><time dateTime={m.createdAt}>{formatChatTime(m.createdAt)}</time></header>
                <p>{m.message}</p>
              </div>
            </div>
          ))}
        </div>

        {sendError && <div className="hg-notice error" style={{ margin: '0 12px 8px' }} role="alert">{sendError}</div>}
        <form className="hg-chat-input" onSubmit={(e) => { e.preventDefault(); send(); }}>
          <textarea
            className="hg-input"
            rows={1}
            placeholder="Type a message..."
            aria-label="Message"
            maxLength={MAX_CHAT_MESSAGE}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            disabled={inv?.status === 'Cancelled'}
          />
          <button type="submit" className="hg-send" aria-label="Send" disabled={sending || !text.trim() || inv?.status === 'Cancelled'}><SendIcon /></button>
        </form>
      </div>
    </main>
  );
};

export default InviteChatPage;
